import { useEffect } from "react";
import { Link } from "react-router-dom";
import { getAllBlogPosts } from "../../lib/blog";
import { applyPageSeo } from "../../lib/seo";

const posts = getAllBlogPosts();

const categoryColors: Record<string, string> = {
  "Template Guide": "bg-sky-100 text-sky-800",
  "Provider Guide": "bg-violet-100 text-violet-800",
  "How-to Guide": "bg-emerald-100 text-emerald-800",
  "Comparison Guide": "bg-amber-100 text-amber-800",
};

export function BlogIndex() {
  useEffect(() => {
    const cleanup = applyPageSeo({
      title: "Blog",
      description:
        "Guides, comparisons, and templates for React Email developers. Learn how to build, customise, and export production-ready email templates with AI.",
      path: "/blog",
      keywords: [
        "react email blog",
        "email template guides",
        "react email tutorials",
        "email development",
        "react email vs mjml",
        "ai email templates",
      ],
    });

    // Add CollectionPage structured data
    const collectionJsonLd = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "React Email Builder Blog",
      "description": "Guides, comparisons, and templates for React Email developers.",
      "url": "https://reactemailbuilder.com/blog",
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(collectionJsonLd);
    script.id = "collection-jsonld";
    document.head.appendChild(script);

    return () => {
      cleanup();
      const existing = document.getElementById("collection-jsonld");
      if (existing) existing.remove();
    };
  }, []);

  return (
    <div className="bg-white text-slate-950">
      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white py-16 sm:py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="mb-4 flex items-center gap-2 text-sm">
            <Link to="/" className="text-slate-400 transition hover:text-slate-600">
              Home
            </Link>
            <svg
              aria-hidden="true"
              className="h-3.5 w-3.5 text-slate-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-slate-700">Blog</span>
          </nav>
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-sky-700">
            Guides & Resources
          </p>
          <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">
            React Email guides, comparisons, and templates.
          </h1>
          <p className="mt-6 text-lg leading-8 text-slate-600 sm:text-xl">
            Practical guides for building, customising, and shipping email
            templates with React Email. Written for developers who want to move
            faster than hand-coding HTML.
          </p>
        </div>
      </section>

      {/* Post list */}
      <section className="py-14 sm:py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {posts.map((post) => (
              <Link
                key={post.slug}
                to={post.href}
                className="group block rounded-md border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-lg sm:p-8"
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`rounded px-3 py-1 text-xs font-semibold ${categoryColors[post.category] ?? "bg-slate-100 text-slate-700"}`}
                  >
                    {post.category}
                  </span>
                  <span className="text-sm text-slate-500">{post.readTime}</span>
                </div>
                <h2 className="mt-4 text-xl font-bold text-slate-950 transition group-hover:text-sky-700 sm:text-2xl">
                  {post.title}
                </h2>
                <p className="mt-3 text-base leading-7 text-slate-600">
                  {post.description}
                </p>
                <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-sky-700">
                  Read article
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 20 20"
                    fill="none"
                    className="h-4 w-4 transition group-hover:translate-x-1"
                  >
                    <path
                      d="M4.166 10h11.667m0 0-4.167-4.167M15.833 10l-4.167 4.167"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-slate-200 bg-slate-950 py-14 text-white sm:py-16">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Start building emails with AI
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-lg text-slate-300">
            148 templates. AI generation. Free tier available. No credit card
            required.
          </p>
          <div className="mt-8 flex justify-center gap-4">
            <Link
              to="/builder"
              className="inline-flex items-center gap-2 rounded bg-white px-6 py-3 text-sm font-semibold text-slate-950 transition hover:bg-slate-100"
            >
              Start building
            </Link>
            <Link
              to="/discover/templates"
              className="inline-flex items-center gap-2 rounded border border-white/40 bg-white/10 px-6 py-3 text-sm font-semibold text-white transition hover:border-white/60 hover:bg-white/15"
            >
              Browse templates
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}